import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Panel, PanelHeader, PanelHeaderBack, Group, Div, Text, Button,
  Spinner, FormItem, Input, Select, Textarea, Snackbar, Headline, Checkbox,
} from '@vkontakte/vkui';
import { Icon24Add, Icon24Delete, Icon24Search } from '@vkontakte/icons';
import { useWeddingStore } from '../../stores/wedding';
import { api } from '../../api/client';
import { ResizableModalPage, sheetId } from '../../ui/ResizableModalPage';
import { ModalRoot, ModalPageHeader } from '@vkontakte/vkui';
import { DragHandle } from '../../ui/DragHandle';
import type { SeatingTable, Guest } from '../../types';
import '../../styles/seating-premium.css';
import SeatingStatsCard from './SeatingStatsCard';
import TableCard from './TableCard';
import UnseatedList from './UnseatedList';
import GuestAvatar from './GuestAvatar';
import CompanionPrompt, { type CompanionPromptState } from './CompanionPrompt';
import { getCompanion } from '../../utils/companions';
import { getTableWarnings, countAllWarnings, type Warning } from '../../utils/seatingWarnings';
import { autoSeat } from '../../utils/autoSeat';
import AutoSeatPreview from './AutoSeatPreview';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';

type Modal = 'form' | 'confirm-delete' | 'guests' | 'menu-form' | 'menu-edit' | 'list-form' | 'auto-seat' | null;

function guestFullName(g: Guest): string {
  return `${g.first_name} ${g.last_name || ''}`.trim();
}
type Tab = 'tables' | 'menus' | 'lists';
type MenuCategory = 'dish' | 'drink' | 'dessert';
type MenuItem = { category: MenuCategory; name: string };

const CAT_LABEL: Record<MenuCategory, string> = {
  dish: 'Блюда',
  drink: 'Напитки',
  dessert: 'Десерты',
};

const CAT_ORDER: MenuCategory[] = ['dish', 'drink', 'dessert'];

export default function SeatingPage() {
  const navigate = useNavigate();
  const { currentWedding, guests, guestGroups, loadGuests, loadGuestGroups, isLoading } = useWeddingStore();

  const [tab, setTab] = useState<Tab>('tables');
  const [tables, setTables] = useState<SeatingTable[]>([]);
  const [menus, setMenus] = useState<{ id: string; name: string; note?: string; items?: MenuItem[] }[]>([]);
  const [lists, setLists] = useState<{ id: string; name: string; note?: string }[]>([]);
  const [editingMenu, setEditingMenu] = useState<{ id: string; name: string; items: MenuItem[] } | null>(null);
  const [newItem, setNewItem] = useState<Record<MenuCategory, string>>({ dish: '', drink: '', dessert: '' });
  const [editItemIndex, setEditItemIndex] = useState<number | null>(null);
  const [editItemName, setEditItemName] = useState('');
  const [openToken, setOpenToken] = useState(0);
  const [modal, setModal] = useState<Modal>(null);
  const [editing, setEditing] = useState<SeatingTable | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<SeatingTable | null>(null);
  const [form, setForm] = useState({ name: '', type: 'round', seats: '8', note: '' });
  const [guestPicker, setGuestPicker] = useState<SeatingTable | null>(null);
  const [pickedGuests, setPickedGuests] = useState<Set<string>>(new Set());
  const [simpleName, setSimpleName] = useState('');
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState<string | null>(null);
  const [prompt, setPrompt] = useState<CompanionPromptState | null>(null);
  const [autoSkipHead, setAutoSkipHead] = useState(true);
  const [undo, setUndo] = useState<{ count: number; ids: string[] } | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState('');

  const base = currentWedding ? `weddings/${currentWedding.id}/seating` : '';

  /** Перезагрузка данных после мутации (backend коммитит явно до ответа). */
  async function refresh() {
    await loadAll();
    await loadGuests().catch(() => {});
  }

  async function loadAll() {
    if (!currentWedding) return;
    try {
      const t = await api.get<SeatingTable[]>(base).catch(() => []);
      setTables(t);
    } catch {
      setSnack('Не удалось загрузить столы');
    }
    try {
      const m = await api.get<{ id: string; name: string; note?: string; items?: MenuItem[] }[]>(`${base}/menus`);
      setMenus(m);
    } catch {
      setMenus([]);
    }
    try {
      const l = await api.get<{ id: string; name: string; note?: string }[]>(`${base}/lists`);
      setLists(l);
    } catch {
      setLists([]);
    }
  }

  useEffect(() => {
    loadGuests().catch(() => {});
    loadGuestGroups().catch(() => {});
    loadAll();
  }, [currentWedding?.id]);

  const guestsByTable = useMemo(() => {
    const m = new Map<string, Guest[]>();
    guests.forEach((g) => {
      const key = g.table_id || '';
      if (!m.has(key)) m.set(key, []);
      m.get(key)!.push(g);
    });
    return m;
  }, [guests]);

  const q = query.trim().toLowerCase();

  const filteredUnseated = useMemo(() => {
    const list = guestsByTable.get('') || [];
    if (!q) return list;
    return list.filter((g) => guestFullName(g).toLowerCase().includes(q));
  }, [guestsByTable, q]);

  const filteredTables = useMemo(() => {
    if (!q) return tables;
    return tables.filter(
      (t) =>
        t.name.toLowerCase().includes(q) ||
        (guestsByTable.get(t.id) || []).some((g) => guestFullName(g).toLowerCase().includes(q)),
    );
  }, [tables, guestsByTable, q]);

  const matchedGuests = useMemo(() => {
    if (!q) return [];
    return guests.filter((g) => guestFullName(g).toLowerCase().includes(q));
  }, [guests, q]);

  const noResults = q !== '' && matchedGuests.length === 0 && filteredTables.length === 0 && filteredUnseated.length === 0;

  function jumpToGuest(g: Guest) {
    const el = document.getElementById(g.table_id ? `st-table-${g.table_id}` : `st-unseated-${g.id}`);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.classList.add('st-hl');
    window.setTimeout(() => el.classList.remove('st-hl'), 1500);
  }

  const groupNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const g of guestGroups) map.set(g.id, g.name);
    return map;
  }, [guestGroups]);

  const warningsByTable = useMemo(() => {
    const m = new Map<string, Warning[]>();
    for (const t of tables) m.set(t.id, getTableWarnings(t, guestsByTable.get(t.id) || [], guests));
    return m;
  }, [tables, guestsByTable, guests]);

  const warningsCount = useMemo(() => countAllWarnings(tables, guestsByTable, guests), [tables, guestsByTable, guests]);

  function scrollToFirstWarning() {
    for (const t of tables) {
      if ((warningsByTable.get(t.id) || []).length > 0) {
        document.getElementById(`st-table-${t.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
    }
  }

  function reassignPairs(t: SeatingTable) {
    const ids = (warningsByTable.get(t.id) || [])
      .filter((w) => w.code === 'split_couple')
      .map((w) => w.guestIds[1]);
    if (ids.length) void assignMany(ids, t.id);
  }

  const guestsById = useMemo(() => {
    const m = new Map<string, Guest>();
    for (const g of guests) m.set(g.id, g);
    return m;
  }, [guests]);

  const autoResult = useMemo(
    () => autoSeat(guests, tables, { skipHeadTable: autoSkipHead, excludeIsCouple: false }),
    [guests, tables, autoSkipHead],
  );

  const autoSeatDisabled = useMemo(() => {
    const free = tables
      .filter((t) => !(autoSkipHead && t.type === 'head'))
      .reduce((s, t) => s + Math.max(0, t.seats - (guestsByTable.get(t.id) || []).length), 0);
    return (guestsByTable.get('')?.length ?? 0) === 0 || free === 0;
  }, [tables, guestsByTable, autoSkipHead]);

  function openAutoSeat() {
    setOpenToken((t) => t + 1);
    setModal('auto-seat');
  }

  async function applyAutoSeat(plan: Map<string, string[]>) {
    const allIds: string[] = [];
    for (const ids of plan.values()) allIds.push(...ids);
    const applied: string[] = [];
    setSaving(true);
    try {
      for (const [tableId, ids] of plan) {
        await api.post(`${base}/assign-guests`, { table_id: tableId, guest_ids: ids });
        applied.push(...ids);
      }
    } catch {
      setSnack(`Применено ${applied.length} из ${allIds.length}`);
      setModal(null);
      await refresh();
      setSaving(false);
      return;
    }
    setModal(null);
    await refresh();
    setSaving(false);
    setUndo({ count: applied.length, ids: applied });
  }

  async function undoAutoSeat() {
    const u = undo;
    setUndo(null);
    if (!u) return;
    await assignMany(u.ids, null);
    setSnack('Рассадка отменена');
  }

  const seatedCount = guests.filter((g) => g.table_id).length;

  const eyebrow = useMemo(() => {
    const name = currentWedding?.name?.trim() || 'Свадьба';
    let date = '';
    if (currentWedding?.wedding_date) {
      const d = new Date(currentWedding.wedding_date);
      if (!Number.isNaN(d.getTime())) date = format(d, 'd MMMM', { locale: ru });
    }
    return date ? `${name} · ${date}` : name;
  }, [currentWedding]);

  function openCreate() {
    setEditing(null);
    setForm({ name: `Стол ${tables.length + 1}`, type: 'round', seats: '8', note: '' });
    setOpenToken((t) => t + 1);
    setModal('form');
  }

  function openEdit(t: SeatingTable) {
    setEditing(t);
    setForm({ name: t.name, type: t.type, seats: String(t.seats), note: t.note || '' });
    setOpenToken((n) => n + 1);
    setModal('form');
  }

  function openGuests(t: SeatingTable) {
    setGuestPicker(t);
    setPickedGuests(new Set((guestsByTable.get(t.id) || []).map((g) => g.id)));
    setOpenToken((n) => n + 1);
    setModal('guests');
  }

  async function handleSave() {
    if (!form.name.trim() || !currentWedding) return;
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        type: form.type,
        seats: Number(form.seats) || 8,
        note: form.note.trim() || undefined,
      };
      if (editing) {
        await api.patch(`${base}/${editing.id}`, payload);
        setSnack('Стол сохранён');
      } else {
        await api.post(base, payload);
        setSnack('Стол добавлен');
      }
      setModal(null);
      setEditing(null);
      await refresh();
    } catch (e) {
      console.error('seating save failed', e);
      setSnack(e instanceof Error ? e.message : 'Не удалось сохранить стол');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!confirmDelete || !currentWedding) return;
    try {
      await api.delete(`${base}/${confirmDelete.id}`);
      setSnack('Стол удалён');
      setConfirmDelete(null);
      setModal(null);
      await refresh();
    } catch {
      setSnack('Не удалось удалить');
    }
  }

  /** Sync guests ↔ table (bulk: заменяет назначение только перечисленных гостей). */
  async function assignMany(guestIds: string[], tableId: string | null) {
    if (!currentWedding || guestIds.length === 0) return;
    try {
      await api.post(`${base}/assign-guests`, { table_id: tableId, guest_ids: guestIds });
      await refresh();
    } catch {
      setSnack('Не удалось рассадить');
    }
  }

  function pickTable(guest: Guest, table: SeatingTable) {
    const filled = (guestsByTable.get(table.id) || []).length;
    const free = table.seats - filled;
    const companion = getCompanion(guest, guests);
    if (companion && companion.table_id && companion.table_id !== table.id) {
      const companionTable = tables.find((t) => t.id === companion.table_id) || null;
      setPrompt({ kind: 'companion-elsewhere', guest, companion, table, companionTable });
      return;
    }
    if (companion && !companion.table_id) {
      if (free >= 2) {
        setPrompt({ kind: 'pair-together', guest, companion, table });
        return;
      }
      if (free === 1) {
        setPrompt({ kind: 'pair-split', guest, companion, table });
        return;
      }
    }
    if (filled >= table.seats) {
      setPrompt({ kind: 'capacity', guest, table, filled });
      return;
    }
    void assignMany([guest.id], table.id);
  }

  function runPromptPrimary() {
    const p = prompt;
    setPrompt(null);
    if (!p) return;
    if (p.kind === 'capacity' || p.kind === 'pair-split') {
      void assignMany([p.guest.id], p.table.id);
    } else if (p.kind === 'pair-together' || p.kind === 'companion-elsewhere') {
      void assignMany([p.guest.id, p.companion.id], p.table.id);
    } else if (p.kind === 'unassign-pair') {
      setPickedGuests((prev) => {
        const next = new Set(prev);
        next.delete(p.guest.id);
        next.delete(p.companion.id);
        return next;
      });
    }
  }

  function runPromptSecondary() {
    const p = prompt;
    setPrompt(null);
    if (!p) return;
    if (p.kind === 'pair-together') {
      void assignMany([p.guest.id], p.table.id);
    } else if (p.kind === 'companion-elsewhere') {
      const filled = (guestsByTable.get(p.table.id) || []).length;
      if (filled >= p.table.seats) {
        setPrompt({ kind: 'capacity', guest: p.guest, table: p.table, filled });
      } else {
        void assignMany([p.guest.id], p.table.id);
      }
    } else if (p.kind === 'unassign-pair') {
      setPickedGuests((prev) => {
        const next = new Set(prev);
        next.delete(p.guest.id);
        return next;
      });
    }
  }

  async function savePickedGuests() {
    if (!guestPicker || !currentWedding) return;
    setSaving(true);
    try {
      const current = new Set((guestsByTable.get(guestPicker.id) || []).map((g) => g.id));
      const toAdd = [...pickedGuests].filter((id) => !current.has(id));
      const toRemove = [...current].filter((id) => !pickedGuests.has(id));
      if (toAdd.length) await api.post(`${base}/assign-guests`, { table_id: guestPicker.id, guest_ids: toAdd });
      if (toRemove.length) await api.post(`${base}/assign-guests`, { table_id: null, guest_ids: toRemove });
      setModal(null);
      setGuestPicker(null);
      setSnack('Рассадка обновлена');
      await refresh();
    } catch {
      setSnack('Не удалось сохранить');
    } finally {
      setSaving(false);
    }
  }

  async function addSimple(kind: 'menus' | 'lists') {
    if (!simpleName.trim() || !currentWedding) return;
    try {
      await api.post(`${base}/${kind}`, { name: simpleName.trim() });
      setSimpleName('');
      setModal(null);
      await refresh();
      setSnack('Добавлено');
    } catch {
      setSnack('Не удалось добавить');
    }
  }

  async function deleteMenu(id: string) {
    if (!currentWedding) return;
    try {
      await api.delete(`${base}/menus/${id}`);
      await refresh();
      setSnack('Меню удалено');
    } catch (e) {
      setSnack(e instanceof Error ? e.message : 'Не удалось удалить меню');
    }
  }

  function openMenuEdit(m: { id: string; name: string; items?: MenuItem[] }) {
    setEditingMenu({ id: m.id, name: m.name, items: [...(m.items || [])] });
    setNewItem({ dish: '', drink: '', dessert: '' });
    setEditItemIndex(null);
    setEditItemName('');
    setOpenToken((t) => t + 1);
    setModal('menu-edit');
  }

  function addMenuItems() {
    if (!editingMenu) return;
    const added: MenuItem[] = [];
    CAT_ORDER.forEach((cat) => {
      const name = newItem[cat].trim();
      if (name) added.push({ category: cat, name });
    });
    if (added.length === 0) return;
    setEditingMenu({
      ...editingMenu,
      items: [...editingMenu.items, ...added],
    });
    setNewItem({ dish: '', drink: '', dessert: '' });
  }

  function removeMenuItem(index: number) {
    if (!editingMenu) return;
    setEditingMenu({
      ...editingMenu,
      items: editingMenu.items.filter((_, i) => i !== index),
    });
  }

  function startEditItem(index: number, name: string) {
    setEditItemIndex(index);
    setEditItemName(name);
  }

  function saveEditItem() {
    if (!editingMenu || editItemIndex == null) return;
    const name = editItemName.trim();
    if (!name) return;
    const items = editingMenu.items.map((it, i) => (i === editItemIndex ? { ...it, name } : it));
    setEditingMenu({ ...editingMenu, items });
    setEditItemIndex(null);
    setEditItemName('');
  }

  async function saveMenuItems() {
    if (!editingMenu || !currentWedding) return;
    setSaving(true);
    try {
      await api.patch(`${base}/menus/${editingMenu.id}`, {
        name: editingMenu.name,
        items: editingMenu.items,
      });
      setModal(null);
      setEditingMenu(null);
      await refresh();
      setSnack('Меню сохранено');
    } catch (e) {
      setSnack(e instanceof Error ? e.message : 'Не удалось сохранить меню');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Panel>
      <PanelHeader before={<PanelHeaderBack onClick={() => navigate('/')} />} />

      <div className="st-root st-page">
        <div className="st-top">
          {tab === 'tables' && (searchOpen ? (
            <div className="st-search">
              <Input
                autoFocus
                placeholder="Поиск по столам и гостям"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              <button
                type="button"
                className="st-icbtn"
                aria-label="Закрыть поиск"
                onClick={() => {
                  setSearchOpen(false);
                  setQuery('');
                }}
              >
                ✕
              </button>
            </div>
          ) : (
            <button type="button" className="st-icbtn" aria-label="Поиск" onClick={() => setSearchOpen(true)}>
              <Icon24Search width={18} height={18} />
            </button>
          ))}
        </div>
        <div className="st-eyebrow">{eyebrow}</div>
        <h1 className="st-h1">Рассадка</h1>

        {/* Tabs */}
        <div className="st-seg" role="tablist">
          {([
            ['tables', 'Столы'],
            ['menus', 'Меню'],
            ['lists', 'Списки'],
          ] as [Tab, string][]).map(([key, label]) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={tab === key}
              className={`st-seg__item${tab === key ? ' st-seg__item--on' : ''}`}
              onClick={() => setTab(key)}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === 'tables' && q && (
          <div className="st-search-results">
            {noResults ? (
              <div className="st-muted" style={{ padding: '8px 2px' }}>Ничего не найдено</div>
            ) : (
              <div className="st-ul">
                {matchedGuests.map((g) => (
                  <button key={g.id} type="button" className="st-li st-li-btn" onClick={() => jumpToGuest(g)}>
                    <GuestAvatar id={g.id} first_name={g.first_name} last_name={g.last_name} size={38} />
                    <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                      <div className="st-li__n">{guestFullName(g)}</div>
                      <div className="st-li__s">
                        {g.table_id
                          ? `за столом ${tables.find((t) => t.id === g.table_id)?.name || ''}`
                          : 'без стола'}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {isLoading && tables.length === 0 ? (
        <Div style={{ textAlign: 'center', padding: 40 }}><Spinner size="l" /></Div>
      ) : tab === 'tables' ? (
        <>
          <SeatingStatsCard
            seated={seatedCount}
            total={guests.length}
            tables={tables.length}
            warnings={warningsCount}
            onWarningsClick={scrollToFirstWarning}
            onAutoSeat={openAutoSeat}
            autoSeatDisabled={autoSeatDisabled}
          />
          <div className="st-sec">
            <h2 className="st-sec__h2">
              Столы<span className="st-sec__count">{tables.length}</span>
            </h2>
            <button type="button" className="st-add" onClick={openCreate}>
              <Icon24Add width={14} height={14} />
              Стол
            </button>
          </div>
          {tables.length === 0 && (
            <div style={{ textAlign: 'center', padding: 24 }}>
              <Text style={{ color: 'var(--st-muted)' }}>Добавьте столы</Text>
            </div>
          )}
          {filteredTables.map((t) => (
            <TableCard
              key={t.id}
              table={t}
              guestsAtTable={guestsByTable.get(t.id) || []}
              warnings={warningsByTable.get(t.id) || []}
              onEdit={openEdit}
              onDelete={(tt) => {
                setConfirmDelete(tt);
                setOpenToken((n) => n + 1);
                setModal('confirm-delete');
              }}
              onOpenGuests={openGuests}
              onReassignPairs={reassignPairs}
            />
          ))}

          <UnseatedList
            guests={filteredUnseated}
            tables={tables}
            guestsByTable={guestsByTable}
            groupNameById={groupNameById}
            onPick={(guest, table) => pickTable(guest, table)}
          />
        </>
      ) : tab === 'menus' ? (
        <>
          <div className="st-sec">
            <h2 className="st-sec__h2">
              Меню<span className="st-sec__count">{menus.length}</span>
            </h2>
            <button
              type="button"
              className="st-add"
              onClick={() => {
                setSimpleName('');
                setOpenToken((t) => t + 1);
                setModal('menu-form');
              }}
            >
              <Icon24Add width={14} height={14} />
              Меню
            </button>
          </div>
          {menus.map((m) => {
            const counts = {
              dish: (m.items || []).filter((i) => i.category === 'dish').length,
              drink: (m.items || []).filter((i) => i.category === 'drink').length,
              dessert: (m.items || []).filter((i) => i.category === 'dessert').length,
            };
            return (
              <div key={m.id} className="st-card" style={{ cursor: 'pointer' }} onClick={() => openMenuEdit(m)}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="st-card__name" style={{ fontSize: 19 }}>
                      {m.name}
                    </div>
                    <div className="st-card__meta">
                      Блюда: {counts.dish} · Напитки: {counts.drink} · Десерты: {counts.dessert}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="st-link"
                    onClick={(e) => {
                      e.stopPropagation();
                      openMenuEdit(m);
                    }}
                  >
                    Изменить
                  </button>
                  <button
                    type="button"
                    className="st-dots"
                    aria-label="Удалить меню"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (window.confirm(`Удалить меню «${m.name}»?`)) void deleteMenu(m.id);
                    }}
                  >
                    <Icon24Delete width={20} height={20} />
                  </button>
                </div>
              </div>
            );
          })}
          {menus.length === 0 && (
            <div className="st-muted" style={{ padding: '4px 2px', fontSize: 13 }}>
              Варианты меню (например: стандарт, вегетарианское, детское)
            </div>
          )}
        </>
      ) : (
        <>
          <div className="st-sec">
            <h2 className="st-sec__h2">
              Списки<span className="st-sec__count">{lists.length}</span>
            </h2>
            <button
              type="button"
              className="st-add"
              onClick={() => {
                setSimpleName('');
                setOpenToken((t) => t + 1);
                setModal('list-form');
              }}
            >
              <Icon24Add width={14} height={14} />
              Список
            </button>
          </div>
          {lists.length > 0 && (
            <div className="st-ul">
              {lists.map((l) => (
                <div key={l.id} className="st-li">
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="st-li__n">{l.name}</div>
                    {l.note && <div className="st-li__s">{l.note}</div>}
                  </div>
                </div>
              ))}
            </div>
          )}
          {lists.length === 0 && (
            <div className="st-muted" style={{ padding: '4px 2px', fontSize: 13 }}>
              Списки рассадки / рассылок
            </div>
          )}
        </>
      )}
      </div>

      <ModalRoot
        activeModal={
          modal === 'form' ? sheetId('seating-form', openToken)
            : modal === 'confirm-delete' ? sheetId('seating-delete', openToken)
              : modal === 'guests' ? sheetId('seating-guests', openToken)
                : modal === 'menu-form' ? sheetId('menu-form', openToken)
                : modal === 'menu-edit' ? sheetId('menu-edit', openToken)
                  : modal === 'list-form' ? sheetId('list-form', openToken)
                    : modal === 'auto-seat' ? sheetId('auto-seat', openToken) : null
        }
        onClose={() => { setModal(null); setEditing(null); setConfirmDelete(null); setGuestPicker(null); }}
      >
        <ResizableModalPage openToken={openToken} id="seating-form" onClose={() => { setModal(null); setEditing(null); }} settlingHeight={80}>
          <ModalPageHeader>{editing ? 'Редактировать стол' : 'Новый стол'}</ModalPageHeader>
          <DragHandle />
          <Group className="st-root">
            <FormItem top="Название *"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></FormItem>
            <FormItem top="Тип">
              <Select value={form.type} onChange={(e) => setForm({ ...form, type: e.currentTarget.value })}
                options={[
                  { label: 'Круглый', value: 'round' },
                  { label: 'Квадратный', value: 'square' },
                  { label: 'Прямоугольный', value: 'rectangular' },
                  { label: 'Президиум', value: 'head' },
                ]} />
            </FormItem>
            <FormItem top="Мест"><Input type="number" value={form.seats} onChange={(e) => setForm({ ...form, seats: e.target.value })} /></FormItem>
            <FormItem top="Заметка"><Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /></FormItem>
            <Div style={{ display: 'flex', gap: 8 }}>
              <Button size="l" mode="primary" stretched disabled={saving} onClick={handleSave}>{saving ? '…' : 'Сохранить'}</Button>
              <Button size="l" mode="secondary" onClick={() => { setModal(null); setEditing(null); }}>Отмена</Button>
            </Div>
          </Group>
        </ResizableModalPage>

        <ResizableModalPage openToken={openToken} id="seating-guests" onClose={() => { setModal(null); setGuestPicker(null); }} settlingHeight={80}>
          <ModalPageHeader>Гости · {guestPicker?.name}</ModalPageHeader>
          <DragHandle />
          <Group className="st-root">
            {guests.map((g) => {
              const companion = getCompanion(g, guests);
              return (
                <Checkbox
                  key={g.id}
                  checked={pickedGuests.has(g.id)}
                  onChange={(e) => {
                    const next = new Set(pickedGuests);
                    if (e.currentTarget.checked) {
                      next.add(g.id);
                      if (companion) next.add(companion.id);
                      setPickedGuests(next);
                    } else if (
                      companion &&
                      next.has(companion.id) &&
                      (guestsByTable.get(guestPicker?.id || '') || []).some((x) => x.id === companion.id)
                    ) {
                      setPrompt({ kind: 'unassign-pair', guest: g, companion });
                    } else {
                      next.delete(g.id);
                      setPickedGuests(next);
                    }
                  }}
                >
                  {g.first_name} {g.last_name}
                  {companion && (
                    <span className="st-muted" style={{ display: 'block', fontSize: 12 }}>
                      пара: {companion.first_name}
                    </span>
                  )}
                </Checkbox>
              );
            })}
            <Div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <Button size="l" mode="primary" stretched disabled={saving} onClick={savePickedGuests}>Сохранить</Button>
              <Button size="l" mode="secondary" onClick={() => { setModal(null); setGuestPicker(null); }}>Отмена</Button>
            </Div>
          </Group>
        </ResizableModalPage>

        <ResizableModalPage openToken={openToken} id="menu-edit" onClose={() => { setModal(null); setEditingMenu(null); }} settlingHeight={80}>
          <ModalPageHeader>Меню · {editingMenu?.name}</ModalPageHeader>
          <DragHandle />
          <Group className="st-root">
            {CAT_ORDER.map((cat) => {
              const all = editingMenu?.items || [];
              const catIndices = all
                .map((x, i) => ({ x, i }))
                .filter(({ x }) => x.category === cat);
              return (
                <div key={cat} style={{ padding: '8px 12px', borderBottom: '1px solid var(--st-border)' }}>
                  <Headline level="2" style={{ marginBottom: 8 }}>{CAT_LABEL[cat]}</Headline>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {catIndices.map(({ x: it, i }) => (
                      <div
                        key={i}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                          padding: '6px 10px',
                          borderRadius: 8,
                          border: '1px solid var(--st-border)',
                          background: 'var(--st-card)',
                          boxShadow: 'var(--st-shadow)',
                          cursor: 'pointer',
                        }}
                        onClick={() => startEditItem(i, it.name)}
                      >
                        {editItemIndex === i ? (
                          <>
                            <Input
                              style={{ width: 120, height: 28 }}
                              value={editItemName}
                              autoFocus
                              onChange={(e) => setEditItemName(e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') saveEditItem();
                                if (e.key === 'Escape') setEditItemIndex(null);
                              }}
                            />
                            <Button size="s" mode="primary" onClick={(e) => { e.stopPropagation(); saveEditItem(); }}>OK</Button>
                          </>
                        ) : (
                          <>
                            <Text style={{ fontSize: 14 }}>{it.name}</Text>
                            <span
                              style={{ color: 'var(--st-muted)', cursor: 'pointer', fontSize: 14 }}
                              onClick={(e) => { e.stopPropagation(); removeMenuItem(i); }}
                            >
                              ×
                            </span>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                    <Input
                      style={{
                        flex: 1,
                        border: 'none',
                        background: 'transparent',
                        boxShadow: 'none',
                      }}
                      value={newItem[cat]}
                      onChange={(e) => setNewItem({ ...newItem, [cat]: e.target.value })}
                      placeholder={`Добавить ${cat === 'dish' ? 'блюдо' : cat === 'drink' ? 'напиток' : 'десерт'}`}
                    />
                    <Button size="s" mode="tertiary" onClick={addMenuItems}>+</Button>
                  </div>
                </div>
              );
            })}
            <Div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
              <Button size="l" mode="primary" stretched disabled={saving} onClick={saveMenuItems}>
                {saving ? '…' : 'Сохранить меню'}
              </Button>
              <Button
                size="l"
                mode="outline"
                onClick={() => {
                  if (editingMenu && window.confirm(`Удалить меню «${editingMenu.name}»?`)) {
                    void deleteMenu(editingMenu.id).then(() => {
                      setModal(null);
                      setEditingMenu(null);
                    });
                  }
                }}
              >
                Удалить меню
              </Button>
              <Button size="l" mode="secondary" onClick={() => { setModal(null); setEditingMenu(null); }}>Отмена</Button>
            </Div>
          </Group>
        </ResizableModalPage>

        <ResizableModalPage openToken={openToken} id="menu-form" onClose={() => setModal(null)} settlingHeight={80}>
          <ModalPageHeader>Новое меню</ModalPageHeader>
          <Group className="st-root">
            <FormItem top="Название *"><Input value={simpleName} onChange={(e) => setSimpleName(e.target.value)} placeholder="Например: Вегетарианское" /></FormItem>
            <Button size="l" mode="primary" stretched onClick={() => void addSimple('menus')}>Добавить</Button>
          </Group>
        </ResizableModalPage>

        <ResizableModalPage openToken={openToken} id="list-form" onClose={() => setModal(null)} settlingHeight={80}>
          <ModalPageHeader>Новый список</ModalPageHeader>
          <Group className="st-root">
            <FormItem top="Название *"><Input value={simpleName} onChange={(e) => setSimpleName(e.target.value)} placeholder="Например: VIP" /></FormItem>
            <Button size="l" mode="primary" stretched onClick={() => void addSimple('lists')}>Добавить</Button>
          </Group>
        </ResizableModalPage>

        <ResizableModalPage openToken={openToken} id="seating-delete" onClose={() => { setModal(null); setConfirmDelete(null); }} settlingHeight={80}>
          <ModalPageHeader>Удалить стол?</ModalPageHeader>
          <Group className="st-root">
            <Div>
              <Text style={{ marginBottom: 12 }}>{confirmDelete?.name}</Text>
              <Button size="l" mode="primary" stretched style={{ marginBottom: 8 }} onClick={handleDelete}>Удалить</Button>
              <Button size="l" mode="secondary" stretched onClick={() => { setModal(null); setConfirmDelete(null); }}>Отмена</Button>
            </Div>
          </Group>
        </ResizableModalPage>

        <AutoSeatPreview
          openToken={openToken}
          plan={autoResult.plan}
          unplaced={autoResult.unplaced}
          tables={tables}
          guestsById={guestsById}
          skipHeadTable={autoSkipHead}
          onToggleSkipHead={setAutoSkipHead}
          onApply={() => void applyAutoSeat(autoResult.plan)}
          onClose={() => setModal(null)}
          saving={saving}
        />
      </ModalRoot>

      {prompt && (
        <CompanionPrompt
          prompt={prompt}
          onPrimary={runPromptPrimary}
          onSecondary={runPromptSecondary}
          onDismiss={() => setPrompt(null)}
        />
      )}

      {undo && (
        <Snackbar
          duration={5000}
          action="Отменить"
          onActionClick={() => void undoAutoSeat()}
          onClose={() => setUndo(null)}
          onClosed={() => setUndo(null)}
        >
          Рассажено {undo.count} гостей
        </Snackbar>
      )}

      {snack && <Snackbar onClose={() => setSnack(null)} onClosed={() => setSnack(null)}>{snack}</Snackbar>}
    </Panel>
  );
}
